import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Download, Eye, CheckCircle2, X, ClipboardList, Trash2, Loader2, Search, RotateCcw, Filter } from "lucide-react";
import { useVendorAuth, vendorFetch } from "./VendorAuthContext";
import { API_BASE } from "../DataContext";

const inr = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2, minimumFractionDigits: 2 });
const fmtDate = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = String(iso).split("-");
  return y && m && d ? `${d}-${m}-${y}` : iso;
};
const fmtQty = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 });

const STATUS_CLR = {
  Submitted: "bg-sky-100 text-sky-700",
  "Under Verification": "bg-amber-100 text-amber-700",
  Approved: "bg-emerald-100 text-emerald-700",
  Rejected: "bg-red-100 text-red-700",
  Paid: "bg-violet-100 text-violet-700",
};

const STATUS_OPTIONS = [
  { value: "All", label: "All Statuses" },
  { value: "Pending", label: "Pending (Submitted / Under Verification)" },
  { value: "Approved", label: "Approved" },
  { value: "Rejected", label: "Rejected" },
  { value: "Paid", label: "Paid" },
];

const PAGE_SIZE = 25;
const EMPTY_FILTERS = { q: "", status: "All", po_number: "", date_from: "", date_to: "" };

export default function VendorInvoices() {
  const { token } = useVendorAuth();
  const [params, setParams] = useSearchParams();
  const showSubmittedFlash = params.get("submitted") === "1";

  // Draft (form) vs applied filters — Fetch button applies drafts
  const [draft, setDraft] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(1);

  const [response, setResponse] = useState(null); // { items, total, page, total_pages }
  const [loading, setLoading] = useState(false);
  const [poOptions, setPoOptions] = useState([]);

  const [viewingInv, setViewingInv] = useState(null);
  const [viewingLoading, setViewingLoading] = useState(false);

  const fetchInvoices = useCallback(async (filters, pageNum) => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({
        q: filters.q, status: filters.status, po_number: filters.po_number,
        date_from: filters.date_from, date_to: filters.date_to,
        page: String(pageNum), page_size: String(PAGE_SIZE),
      });
      const r = await vendorFetch(token)(`/api/vendor/invoices?${qs.toString()}`);
      if (!r.ok) throw new Error("Failed to load invoices");
      setResponse(await r.json());
    } catch (e) {
      setResponse({ items: [], total: 0, page: 1, total_pages: 1, error: e.message });
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Load PO number options once
  useEffect(() => {
    vendorFetch(token)("/api/vendor/invoices-filters")
      .then((r) => r.json()).then((d) => setPoOptions(d.po_numbers || []))
      .catch(() => setPoOptions([]));
  }, [token]);

  // Fetch when applied filters or page change
  useEffect(() => {
    fetchInvoices(applied, page);
  }, [applied, page, fetchInvoices]);

  const applyFilters = () => { setApplied(draft); setPage(1); };
  const clearFilters = () => { setDraft(EMPTY_FILTERS); setApplied(EMPTY_FILTERS); setPage(1); };
  const onSearchEnter = (e) => { if (e.key === "Enter") applyFilters(); };

  const total = response?.total ?? 0;
  const totalPages = response?.total_pages ?? 1;
  const items = response?.items ?? [];
  const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, total);

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (applied.q) n++;
    if (applied.status && applied.status !== "All") n++;
    if (applied.po_number) n++;
    if (applied.date_from) n++;
    if (applied.date_to) n++;
    return n;
  }, [applied]);

  const downloadPdf = async (inv) => {
    const r = await fetch(`${API_BASE}/api/vendor/invoices/${inv.id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) return alert("PDF download failed");
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = inv.pdf_filename || `${inv.invoice_number}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const deleteInvoice = async (inv) => {
    if (!confirm(`Delete invoice ${inv.invoice_number} (#${inv.invoice_ref})? This cannot be undone.`)) return;
    const r = await fetch(`${API_BASE}/api/vendor/invoices/${inv.id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) return alert((await r.json().catch(() => ({}))).detail || "Delete failed");
    fetchInvoices(applied, page);
  };

  const openLineItems = async (inv) => {
    setViewingLoading(true);
    setViewingInv({ ...inv, line_items: null });
    try {
      const r = await vendorFetch(token)(`/api/vendor/invoices/${inv.id}`);
      if (!r.ok) throw new Error("Failed to load invoice details");
      setViewingInv(await r.json());
    } catch (e) {
      alert(e.message); setViewingInv(null);
    } finally {
      setViewingLoading(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="vendor-invoices">
      <div>
        <h1 className="text-2xl md:text-3xl font-black text-[#0a2a5e]">Invoice Status</h1>
        <p className="text-sm text-slate-500 mt-1">Track, search and filter every invoice you have submitted.</p>
      </div>

      {showSubmittedFlash && (
        <div className="flex items-start gap-3 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl px-4 py-3 text-sm" data-testid="submit-flash">
          <CheckCircle2 className="h-5 w-5 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <div className="font-semibold">Invoice submitted successfully</div>
            <div className="text-xs mt-0.5">The BAISHALII team will review your invoice. You'll see the status update here.</div>
          </div>
          <button onClick={() => setParams({})} className="text-emerald-700 hover:text-emerald-900"><X className="h-4 w-4" /></button>
        </div>
      )}

      {/* ============= Filters ============= */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 md:p-5 space-y-3" data-testid="invoice-filters">
        {/* Search bar */}
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search by Invoice No, PO No, or Invoice ID…"
              value={draft.q}
              onChange={(e) => setDraft({ ...draft, q: e.target.value })}
              onKeyDown={onSearchEnter}
              data-testid="inv-search-input"
              className="w-full rounded-lg border border-slate-200 pl-10 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0a2a5e]/20 focus:border-[#0a2a5e]"
            />
          </div>
          {activeFilterCount > 0 && (
            <span className="hidden md:inline-flex items-center gap-1 text-xs text-slate-500 whitespace-nowrap">
              <Filter className="h-3 w-3" /> {activeFilterCount} active
            </span>
          )}
        </div>

        {/* Filter row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Invoice Date — From</label>
            <input
              type="date"
              value={draft.date_from}
              onChange={(e) => setDraft({ ...draft, date_from: e.target.value })}
              data-testid="inv-date-from"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0a2a5e]/20 focus:border-[#0a2a5e]"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Invoice Date — To</label>
            <input
              type="date"
              value={draft.date_to}
              onChange={(e) => setDraft({ ...draft, date_to: e.target.value })}
              data-testid="inv-date-to"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0a2a5e]/20 focus:border-[#0a2a5e]"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Status</label>
            <select
              value={draft.status}
              onChange={(e) => setDraft({ ...draft, status: e.target.value })}
              data-testid="inv-status-filter"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0a2a5e]/20 focus:border-[#0a2a5e]"
            >
              {STATUS_OPTIONS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">PO Number</label>
            <input
              list="vendor-po-list"
              value={draft.po_number}
              onChange={(e) => setDraft({ ...draft, po_number: e.target.value })}
              placeholder="All POs"
              data-testid="inv-po-filter"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0a2a5e]/20 focus:border-[#0a2a5e]"
            />
            <datalist id="vendor-po-list">
              {poOptions.map((p) => (<option key={p} value={p} />))}
            </datalist>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-1">
          <button
            onClick={clearFilters}
            data-testid="inv-clear-filters"
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
          >
            <RotateCcw className="h-4 w-4" /> Clear Filters
          </button>
          <button
            onClick={applyFilters}
            data-testid="inv-fetch"
            className="inline-flex items-center gap-1.5 rounded-lg bg-[#0a2a5e] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0a2a5e]/90"
          >
            <Search className="h-4 w-4" /> Fetch
          </button>
        </div>
      </div>

      {/* ============= Table ============= */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead>
            <tr className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="px-4 py-3 font-semibold">ID</th>
              <th className="px-4 py-3 font-semibold">Invoice No</th>
              <th className="px-4 py-3 font-semibold">PO Number</th>
              <th className="px-4 py-3 font-semibold">Submitted On</th>
              <th className="px-4 py-3 font-semibold">Invoice Date</th>
              <th className="px-4 py-3 font-semibold text-right">Amount</th>
              <th className="px-4 py-3 font-semibold">Status</th>
              <th className="px-4 py-3 font-semibold text-center">View Items</th>
              <th className="px-4 py-3 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-400"><Loader2 className="h-4 w-4 inline animate-spin mr-2" />Loading…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={9} className="px-4 py-12 text-center">
                  <div className="mx-auto h-14 w-14 rounded-full bg-slate-100 flex items-center justify-center mb-3">
                    <ClipboardList className="h-6 w-6 text-slate-400" />
                  </div>
                  <p className="font-semibold text-slate-700 mb-1">
                    {activeFilterCount > 0 ? "No invoices match your filters" : "No invoices submitted yet"}
                  </p>
                  <p className="text-sm text-slate-500">
                    {activeFilterCount > 0
                      ? <>Try adjusting the filters or <button onClick={clearFilters} className="text-[#0a2a5e] hover:underline font-semibold">clear all filters</button>.</>
                      : <>Go to <b>Submit Invoice</b> to raise your first invoice against an open PO.</>}
                  </p>
                </td>
              </tr>
            )}
            {!loading && items.map((inv) => (
              <tr key={inv.id} className="border-t border-slate-100 hover:bg-sky-50/50">
                <td className="px-4 py-3 font-mono text-xs font-bold text-slate-500">#{inv.invoice_ref || "—"}</td>
                <td className="px-4 py-3 font-semibold text-[#0a2a5e]">{inv.invoice_number}</td>
                <td className="px-4 py-3 font-mono text-xs">{inv.po_number}</td>
                <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{fmtDate((inv.submitted_at || "").slice(0, 10))}</td>
                <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{fmtDate(inv.invoice_date)}</td>
                <td className="px-4 py-3 text-right font-semibold whitespace-nowrap">₹ {inr(inv.invoice_amount)}</td>
                <td className="px-4 py-3">
                  <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS_CLR[inv.status] || "bg-slate-100"}`}>{inv.status}</span>
                </td>
                <td className="px-4 py-3 text-center">
                  <button
                    onClick={() => openLineItems(inv)}
                    data-testid={`inv-view-items-${inv.id}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-200 bg-white text-slate-700 hover:bg-[#0a2a5e] hover:text-white hover:border-[#0a2a5e] text-xs font-semibold transition-colors"
                    title="View invoice line items"
                  >
                    <Eye className="h-3.5 w-3.5" /> View Items
                  </button>
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => downloadPdf(inv)} data-testid={`inv-pdf-${inv.id}`} className="text-emerald-600 hover:text-emerald-800 p-1.5" title="Download invoice PDF"><Download className="h-4 w-4" /></button>
                  {inv.status === "Submitted" && (
                    <button onClick={() => deleteInvoice(inv)} data-testid={`inv-delete-${inv.id}`} className="text-red-500 hover:text-red-700 p-1.5" title="Delete invoice"><Trash2 className="h-4 w-4" /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ============= Pagination Footer ============= */}
      {response && (
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pt-1" data-testid="invoice-pagination">
          <div className="text-sm text-slate-600">
            <span className="font-semibold text-slate-800">Page {response.page} of {totalPages}</span>
            <span className="mx-2 text-slate-300">|</span>
            <span>Total Entries: <span className="font-semibold text-slate-800">{total}</span></span>
            {total > 0 && (
              <>
                <span className="mx-2 text-slate-300">|</span>
                <span className="text-slate-500">Showing {rangeStart}–{rangeEnd}</span>
              </>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(1)}
              disabled={page === 1 || loading}
              data-testid="page-first"
              className="px-2.5 py-1.5 rounded-md border border-slate-200 bg-white text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >« First</button>
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1 || loading}
              data-testid="page-prev"
              className="px-3 py-1.5 rounded-md border border-slate-200 bg-white text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >‹ Prev</button>
            <span className="px-3 py-1.5 text-sm font-semibold text-slate-700">{response.page} / {totalPages}</span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || loading}
              data-testid="page-next"
              className="px-3 py-1.5 rounded-md border border-slate-200 bg-white text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >Next ›</button>
            <button
              onClick={() => setPage(totalPages)}
              disabled={page >= totalPages || loading}
              data-testid="page-last"
              className="px-2.5 py-1.5 rounded-md border border-slate-200 bg-white text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >Last »</button>
          </div>
        </div>
      )}

      {viewingInv && (
        <LineItemsModal
          invoice={viewingInv}
          loading={viewingLoading}
          onClose={() => setViewingInv(null)}
        />
      )}
    </div>
  );
}

function LineItemsModal({ invoice, loading, onClose }) {
  const items = invoice.line_items || [];
  const misc = invoice.misc_charges || [];
  const itemsTotal = items.reduce((s, it) => s + Number(it.value || 0), 0);
  const miscBasic = Number(invoice.misc_basic || 0);
  const miscGst = Number(invoice.misc_gst || 0);
  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="invoice-line-items-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden"
      >
        <div className="px-6 py-4 border-b border-slate-100 flex items-start justify-between">
          <div>
            <h3 className="text-lg font-bold text-[#0a2a5e]">Invoice Line Items</h3>
            <div className="text-sm text-slate-500 mt-1">
              <span>Invoice No.: <span className="font-semibold text-slate-800">{invoice.invoice_number}</span></span>
              <span className="mx-3 text-slate-300">|</span>
              <span>Dated: <span className="font-semibold text-slate-800">{fmtDate(invoice.invoice_date)}</span></span>
            </div>
          </div>
          <button onClick={onClose} data-testid="line-items-close" className="text-slate-400 hover:text-slate-700 p-1 rounded-md hover:bg-slate-100" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading || !invoice.line_items ? (
            <div className="flex items-center justify-center py-16 text-slate-400 text-sm">
              <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading line items…
            </div>
          ) : items.length === 0 ? (
            <div className="py-16 text-center text-slate-400 text-sm">No line items recorded for this invoice.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-slate-50 border-b border-slate-100">
                <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-3 font-semibold">PO Number</th>
                  <th className="px-4 py-3 font-semibold">PO Item</th>
                  <th className="px-4 py-3 font-semibold">Description</th>
                  <th className="px-4 py-3 font-semibold text-right">Quantity</th>
                  <th className="px-4 py-3 font-semibold">Unit</th>
                  <th className="px-4 py-3 font-semibold text-right">Rate Per Unit</th>
                  <th className="px-4 py-3 font-semibold text-right">Value</th>
                </tr>
              </thead>
              <tbody>
                {items.map((ln, idx) => (
                  <tr key={idx} className="border-t border-slate-100" data-testid={`line-item-row-${idx}`}>
                    <td className="px-4 py-3 font-mono text-xs">{invoice.po_number}</td>
                    <td className="px-4 py-3 font-semibold text-slate-800">{ln.po_item || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{ln.description || "—"}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">{fmtQty(ln.quantity)}</td>
                    <td className="px-4 py-3 text-slate-600">{ln.unit || "—"}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">₹ {inr(ln.rate)}</td>
                    <td className="px-4 py-3 text-right font-semibold whitespace-nowrap">₹ {inr(ln.value)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-slate-200 bg-slate-50">
                  <td colSpan={6} className="px-4 py-3 text-right font-semibold text-slate-700">Products Basic</td>
                  <td className="px-4 py-3 text-right font-bold text-[#0a2a5e] whitespace-nowrap">₹ {inr(itemsTotal)}</td>
                </tr>
              </tfoot>
            </table>
          )}
          {misc.length > 0 && (
            <div className="mt-4 border border-amber-200 rounded-lg overflow-hidden" data-testid="invoice-misc-charges">
              <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 text-xs font-bold uppercase tracking-wide text-amber-800">
                Miscellaneous / Additional Charges
              </div>
              <table className="w-full text-sm">
                <thead className="bg-amber-50/50 text-left text-xs uppercase tracking-wide text-amber-800">
                  <tr>
                    <th className="px-4 py-2 font-semibold">Description</th>
                    <th className="px-4 py-2 font-semibold text-right">Qty</th>
                    <th className="px-4 py-2 font-semibold text-right">Rate</th>
                    <th className="px-4 py-2 font-semibold text-right">GST %</th>
                    <th className="px-4 py-2 font-semibold text-right">Basic</th>
                    <th className="px-4 py-2 font-semibold text-right">GST</th>
                    <th className="px-4 py-2 font-semibold text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {misc.map((m, i) => (
                    <tr key={i} className="border-t border-amber-100" data-testid={`misc-row-${i}`}>
                      <td className="px-4 py-2 font-semibold text-slate-800">{m.description}</td>
                      <td className="px-4 py-2 text-right whitespace-nowrap">{fmtQty(m.qty)}</td>
                      <td className="px-4 py-2 text-right whitespace-nowrap">₹ {inr(m.rate)}</td>
                      <td className="px-4 py-2 text-right whitespace-nowrap">{m.gst_pct}%</td>
                      <td className="px-4 py-2 text-right whitespace-nowrap">₹ {inr(m.basic)}</td>
                      <td className="px-4 py-2 text-right whitespace-nowrap">₹ {inr(m.gst_amount)}</td>
                      <td className="px-4 py-2 text-right font-semibold whitespace-nowrap">₹ {inr(m.amount)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-amber-200 bg-amber-50">
                    <td colSpan={6} className="px-4 py-2 text-right font-semibold text-amber-800">Misc Total</td>
                    <td className="px-4 py-2 text-right font-bold text-amber-900 whitespace-nowrap">₹ {inr(miscBasic + miscGst)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
          <div className="mt-4 bg-slate-50 border border-slate-200 rounded-lg p-3 max-w-sm ml-auto text-sm">
            <div className="flex justify-between py-0.5"><span>Products Basic</span><span>₹ {inr(itemsTotal)}</span></div>
            {miscBasic > 0 && <div className="flex justify-between py-0.5"><span>Misc Basic</span><span>₹ {inr(miscBasic)}</span></div>}
            {miscGst > 0 && <div className="flex justify-between py-0.5 text-slate-600"><span>Misc GST</span><span>₹ {inr(miscGst)}</span></div>}
            <div className="flex justify-between border-t-2 border-[#0a2a5e] mt-2 pt-2 font-bold text-[#0a2a5e]"><span>Invoice Total</span><span>₹ {inr(invoice.invoice_amount)}</span></div>
          </div>
        </div>

        <div className="px-6 py-3 border-t border-slate-100 flex justify-end bg-slate-50">
          <button onClick={onClose} data-testid="line-items-close-btn" className="px-4 py-2 rounded-md bg-[#0a2a5e] text-white text-sm font-semibold hover:bg-[#0a2a5e]/90">Close</button>
        </div>
      </div>
    </div>
  );
}
